import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { API_BASE } from '@/lib/config'

const TAB_MAP: Record<string, string> = {
  reports: 'reports',
  appeals: 'reports',
  moderation: 'reports',
  cases: 'cases',
  users: 'users',
  announcements: 'announcements',
  status: 'status',
  analytics: 'analytics',
  blog: 'blog',
  safety: 'safety',
  audit: 'audit',
  staff: 'staff',
  dashboard: 'dashboard',
  detail: 'detail',
}

const TAB_LOADERS: Record<string, keyof NonNullable<Window['clyrTs']>> = {
  dashboard: 'loadStats',
  reports: 'loadReports',
  cases: 'loadCases',
  announcements: 'loadAnnouncements',
  status: 'loadStatusAdmin',
  analytics: 'loadAnalytics',
  blog: 'loadBlogAdmin',
  safety: 'loadSafetyControls',
  audit: 'loadAudit',
  staff: 'loadStaff',
}

function loadScript(src: string, reload = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (reload) {
        existing.remove()
        delete window.clyrTs
      } else {
        if ((existing as HTMLScriptElement).dataset.clyrLoaded === 'true') {
          resolve()
        } else {
          existing.addEventListener('load', () => resolve(), { once: true })
          existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true })
        }
        return
      }
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => {
      s.dataset.clyrLoaded = 'true'
      resolve()
    }
    s.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.body.appendChild(s)
  })
}

export function TrustSafetyHost({
  tab = 'dashboard',
  appealFilter,
  targetFilter,
}: {
  tab?: string
  appealFilter?: boolean
  targetFilter?: 'message' | 'dm_message'
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const mountId = useRef(0)
  const { search } = useLocation()
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [loadError, setLoadError] = useState('')
  const [retryNonce, setRetryNonce] = useState(0)

  useEffect(() => {
    window.CLYR_API_BASE = API_BASE
  }, [])

  useEffect(() => {
    const id = ++mountId.current
    let cancelled = false

    async function init() {
      if (!rootRef.current) return
      setLoadState('loading')
      setLoadError('')
      try {
        window.clyrTsAppealsOnly = !!appealFilter

        await loadScript('/scripts/blog-markdown.js')
        await loadScript('/scripts/trust-safety-admin.js', true)
        if (cancelled || mountId.current !== id) return

        const panel = TAB_MAP[tab] || tab
        const ts = window.clyrTs
        if (!ts) throw new Error('The moderation module did not initialize.')

        ts.showPanel(panel)
        if (panel === 'reports') {
          const statusEl = document.getElementById('ts-filter-status') as HTMLSelectElement | null
          const targetEl = document.getElementById('ts-filter-target') as HTMLSelectElement | null
          if (appealFilter && statusEl) statusEl.value = 'open'
          if (targetFilter && targetEl) targetEl.value = targetFilter
        }
        const loader = TAB_LOADERS[panel]
        if (loader && typeof ts[loader] === 'function') {
          ;(ts[loader] as () => void)()
        }

        if (panel === 'users') {
          const query = new URLSearchParams(search).get('q') || ''
          const input = document.getElementById('ts-user-search') as HTMLInputElement | null
          if (input && query) {
            input.value = query
            ts.searchUsers?.()
          }
        }

        setLoadState('ready')
      } catch (error) {
        if (cancelled || mountId.current !== id) return
        setLoadError(error instanceof Error ? error.message : 'The admin module could not be loaded.')
        setLoadState('error')
      }
    }

    void init()
    return () => {
      cancelled = true
    }
  }, [tab, appealFilter, targetFilter, search, retryNonce])

  return (
    <div ref={rootRef} className="admin-legacy-host clyr-theme ts-page">
      {loadState !== 'ready' && (
        <div className="admin-module-state" role={loadState === 'error' ? 'alert' : 'status'}>
          {loadState === 'loading' ? (
            <><span className="admin-module-spinner" aria-hidden="true" /><p>Loading admin tools…</p></>
          ) : (
            <>
              <strong>Couldn’t load this admin tool</strong>
              <p>{loadError}</p>
              <button type="button" onClick={() => setRetryNonce((value) => value + 1)}>Try again</button>
            </>
          )}
        </div>
      )}
      <div id="ts-loading" hidden />
      <div id="ts-denied" hidden />
      <div id="ts-app" className="ts-app">
        <aside className="ts-sidebar" hidden>
          <nav className="ts-nav">
            {Object.keys(TAB_MAP).map((t) => (
              <button key={t} type="button" className="ts-nav-btn" data-tab={t} />
            ))}
          </nav>
        </aside>
        <section className="ts-content">
          <div className="ts-panel" id="ts-panel-dashboard">
            <div className="ts-stats-grid" id="ts-stats-grid" />
          </div>
          <div className="ts-panel" id="ts-panel-reports" hidden>
            <div className="ts-panel-head">
              <h2>Reports</h2>
              <div className="ts-filters">
                <select id="ts-filter-status">
                  <option value="">All statuses</option>
                  <option value="open">Open</option>
                  <option value="triage">Triage</option>
                  <option value="investigating">In review</option>
                  <option value="escalated">Escalated</option>
                  <option value="action_taken">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <select id="ts-filter-priority">
                  <option value="">All severity</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
                <select id="ts-filter-target">
                  <option value="">All content types</option>
                  <option value="message">Message</option>
                  <option value="dm_message">DM</option>
                  <option value="user">User</option>
                  <option value="zone">Zone</option>
                </select>
                <select id="ts-filter-assigned">
                  <option value="">All</option>
                  <option value="me">Assigned to me</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="mod_queue" id="ts-opt-mod-queue" hidden>My mod queue</option>
                </select>
                <button type="button" className="btn btn-secondary" id="ts-merge-reports" hidden>Merge selected</button>
                <button type="button" className="btn btn-secondary" id="ts-refresh-reports">Refresh</button>
              </div>
            </div>
            <div className="ts-report-list" id="ts-report-list" />
          </div>
          <div className="ts-panel" id="ts-panel-detail" hidden>
            <button type="button" className="ts-back-btn" id="ts-back-reports">← Back to reports</button>
            <div id="ts-report-detail" />
          </div>
          <div className="ts-panel" id="ts-panel-cases" hidden>
            <h2>Report cases</h2>
            <p className="ts-hint">Merged duplicate reports grouped into investigation cases.</p>
            <div id="ts-cases-list" />
          </div>
          <div className="ts-panel" id="ts-panel-safety" hidden>
            <h2>Safety &amp; enforcement controls</h2>
            <div id="ts-safety-controls" />
          </div>
          <div className="ts-panel" id="ts-panel-users" hidden>
            <h2>User management</h2>
            <div className="ts-search-row">
              <input type="search" id="ts-user-search" placeholder="Search username, email…" />
              <button type="button" className="btn btn-primary" id="ts-user-search-btn">Search</button>
            </div>
            <div id="ts-user-results" />
            <div id="ts-user-profile" hidden />
          </div>
          <div className="ts-panel" id="ts-panel-announcements" hidden>
            <div className="ts-panel-head">
              <h2>System announcements</h2>
              <button type="button" className="btn btn-primary" id="ts-new-announcement">New announcement</button>
            </div>
            <div id="ts-announcements-list" />
            <div id="ts-announcement-form-wrap" hidden>
              <div className="ts-user-profile-card" id="ts-announcement-form" />
            </div>
          </div>
          <div className="ts-panel" id="ts-panel-status" hidden>
            <h2>Status page</h2>
            <div className="ts-user-profile-card">
              <div className="ts-panel-head">
                <h4>Services</h4>
                <button type="button" className="btn btn-primary" id="svc-show-add">Add service</button>
              </div>
              <div id="ts-status-services-list" />
              <div id="ts-status-service-form-wrap" hidden>
                <div className="ts-status-service-form" id="ts-status-service-form" />
              </div>
            </div>
            <div className="ts-user-profile-card">
              <h4>Post incident</h4>
              <div className="ts-admin-field"><label>Title</label><input type="text" id="inc-title" /></div>
              <div className="ts-admin-field"><label>Initial update</label><textarea id="inc-body" rows={3} /></div>
              <div className="ts-admin-grid">
                <div className="ts-admin-field"><label>Impact</label>
                  <select id="inc-impact"><option value="minor">Minor</option><option value="major">Major</option><option value="critical">Critical</option></select>
                </div>
                <div className="ts-admin-field"><label>Status</label>
                  <select id="inc-status"><option value="investigating">Investigating</option><option value="identified">Identified</option><option value="monitoring">Monitoring</option></select>
                </div>
              </div>
              <div className="ts-admin-field"><label>Affected services</label><div id="inc-affected-services" className="ts-service-checks" /></div>
              <button type="button" className="btn btn-primary" id="inc-create">Create incident</button>
            </div>
            <div className="ts-user-profile-card">
              <h4>Schedule maintenance</h4>
              <div className="ts-admin-field"><label>Title</label><input type="text" id="maint-title" /></div>
              <div className="ts-admin-field"><label>Description</label><textarea id="maint-desc" rows={2} /></div>
              <div className="ts-admin-grid">
                <div className="ts-admin-field"><label>Starts</label><input type="datetime-local" id="maint-start" /></div>
                <div className="ts-admin-field"><label>Ends</label><input type="datetime-local" id="maint-end" /></div>
              </div>
              <div className="ts-admin-field"><label>Affected services</label><div id="maint-affected-services" className="ts-service-checks" /></div>
              <button type="button" className="btn btn-primary" id="maint-create">Schedule</button>
            </div>
            <div id="ts-status-preview" className="ts-hint" />
          </div>
          <div className="ts-panel" id="ts-panel-analytics" hidden>
            <h2>Platform analytics</h2>
            <div id="ts-analytics-content" />
          </div>
          <div className="ts-panel" id="ts-panel-blog" hidden>
            <div className="ts-panel-head">
              <h2>Blog</h2>
            </div>
            <div className="ts-user-profile-card" id="ts-blog-editor-wrap">
              <h4 id="ts-blog-form-title">New post</h4>
              <input type="hidden" id="ts-blog-edit-id" defaultValue="" />
              <div className="ts-admin-grid">
                <div className="ts-admin-field"><label>Title</label><input type="text" id="ts-blog-title" /></div>
                <div className="ts-admin-field"><label>URL slug</label><input type="text" id="ts-blog-slug" /></div>
              </div>
              <div className="ts-admin-field"><label>Excerpt</label><input type="text" id="ts-blog-excerpt" /></div>
              <div className="ts-admin-field"><label>Cover image URL</label><input type="url" id="ts-blog-cover" /></div>
              <div className="ts-admin-field"><label>Status</label>
                <select id="ts-blog-status"><option value="draft">Draft</option><option value="published">Published</option></select>
              </div>
              <div className="ts-blog-editor-split">
                <div className="ts-blog-editor-pane">
                  <label htmlFor="ts-blog-body">Body (Markdown)</label>
                  <textarea id="ts-blog-body" rows={16} />
                </div>
                <div className="ts-blog-editor-pane">
                  <label>Preview</label>
                  <div id="ts-blog-preview" className="blog-prose ts-blog-preview" />
                </div>
              </div>
              <div className="ts-admin-actions">
                <button type="button" className="btn btn-primary" id="ts-blog-save">Save post</button>
                <button type="button" className="btn btn-secondary" id="ts-blog-cancel" hidden>Cancel edit</button>
              </div>
            </div>
            <div id="ts-blog-posts-list" className="ts-blog-posts-list" />
          </div>
          <div className="ts-panel" id="ts-panel-audit" hidden>
            <h2>Audit log</h2>
            <div className="ts-search-row">
              <input type="search" id="ts-audit-search" placeholder="Search…" />
              <select id="ts-audit-action-filter">
                <option value="">All actions</option>
                <option value="ban">ban</option>
                <option value="suspend">suspend</option>
                <option value="escalate">escalate</option>
                <option value="reset_password">reset_password</option>
                <option value="platform_settings_update">platform_settings</option>
              </select>
              <button type="button" className="btn btn-secondary" id="ts-audit-search-btn">Search</button>
              <a className="btn btn-secondary" id="ts-audit-export" href="#">Export CSV</a>
            </div>
            <div className="ts-audit-list" id="ts-audit-list" />
          </div>
          <div className="ts-panel" id="ts-panel-staff" hidden>
            <h2>Staff roster</h2>
            <div id="ts-staff-list" />
          </div>
        </section>
      </div>
      <a href="#" id="nav-logout" hidden />
      <a href="careers-admin.html" id="nav-careers-admin" hidden />
      <p id="ts-role-badge" hidden />
    </div>
  )
}
